import {
  MachinesListCompanyEntry,
  MachinesListMachineTypes,
} from "@src/types/gateways-with-machines";

const normalizeSearchTerms = (searchTerms: string[]): string[] =>
  searchTerms.map((term) => term.toLowerCase()).filter(Boolean);

const valueMatchesSearchTerm = (
  value: string | number | boolean | null | undefined,
  searchTermValue: string,
): boolean => {
  if (value == null) return false;
  return String(value).toLowerCase().includes(searchTermValue);
};

const machineMatchesSearchTerms = (
  machine: MachinesListMachineTypes,
  companyName: string,
  normalizedSearchTerms: string[],
): boolean => {
  if (!normalizedSearchTerms.length) return false;

  const searchableValues = [
    machine.name,
    machine.machine_id,
    machine.ip_address,
    machine.vendor_id,
    companyName,
  ];

  return normalizedSearchTerms.every((term) =>
    searchableValues.some((value) => valueMatchesSearchTerm(value, term)),
  );
};

const updateGatewayMachines = (
  gateway: MachinesListCompanyEntry,
  machines: Record<string, MachinesListMachineTypes>,
): MachinesListCompanyEntry => ({
  ...gateway,
  machines,
  sources_visible: true,
});

export const findSearchedMachinesList = (
  data: Record<string, MachinesListCompanyEntry>,
  searchTerms: string[],
): Record<string, MachinesListCompanyEntry> => {
  const normalizedSearchTerms = normalizeSearchTerms(searchTerms);
  if (!normalizedSearchTerms.length) {
    return {};
  }

  const entries = Object.values(data ?? {});
  if (!entries.length) {
    return {};
  }

  const results: Record<string, MachinesListCompanyEntry> = {};

  for (const companyEntry of entries) {
    const machines = companyEntry?.machines ?? {};
    const matchedMachines: Record<string, MachinesListMachineTypes> = {};
    let isMatching = false;

    for (const machine of Object.values(machines)) {
      if (
        machineMatchesSearchTerms(
          machine,
          companyEntry.name,
          normalizedSearchTerms,
        )
      ) {
        matchedMachines[machine.machine_id] = machine;
        isMatching = true;
      }
    }

    if (isMatching) {
      results[companyEntry.name] = updateGatewayMachines(
        companyEntry,
        matchedMachines,
      );
    }
  }

  return results;
};
